import { CommonModule } from '@angular/common';
import { Component, DestroyRef, ViewChild, inject, signal } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged } from 'rxjs';

import { EntitySchemaService } from '../../services/entity-schema.service';
import { CatalogService, EntitySchemaCatalogItem } from '../../services/catalog.service';
import type { EntityFieldRow, ViewFieldNodeState } from './mapping-types';
import { ReteCanvasComponent } from './rete/rete-canvas.component';
import type { ReteConnection } from './rete/rete-canvas.component';
import { EntityFieldPaletteComponent } from './rete/palette/entity-field-palette.component';
import type { ComparatorOp } from './rete/nodes/comparator-control.component';
import type { LiteralKind } from './rete/nodes/literal-control.component';
import type { FormulaFn } from './rete/nodes/formula-control.component';

type ViewSchemaOut = {
  fields?: Array<{ fieldName: string; formula?: string; fieldType?: string }>;
  rowExpands?: string[];
};

@Component({
  selector: 'app-mapping-screen',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ReteCanvasComponent,
    EntityFieldPaletteComponent,
  ],
  templateUrl: './mapping-screen.component.html',
  styleUrl: './mapping-screen.component.scss',
})
export class MappingScreenComponent {
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly entitySchemaService = inject(EntitySchemaService);
  private readonly catalogService = inject(CatalogService);

  @ViewChild(ReteCanvasComponent) private reteCanvas?: ReteCanvasComponent;

  protected readonly loadingEntitySchema = signal(false);
  protected readonly entitySchemaError = signal<string | null>(null);

  protected readonly loadingCatalog = signal(false);
  protected readonly catalogError = signal<string | null>(null);
  protected readonly catalogItems = signal<EntitySchemaCatalogItem[]>([]);

  protected readonly entityFields = signal<EntityFieldRow[]>([]);
  protected readonly rowExpands = signal<string[]>([]);

  protected readonly viewNodes = signal<ViewFieldNodeState[]>([]);
  protected readonly selectedViewNodeId = signal<string | null>(null);

  protected readonly connections = signal<ReteConnection[]>([]);

  protected readonly viewSchemaJson = signal<string>('');

  protected readonly form = this.fb.group({
    entitySchemaId: ['', Validators.required],
    schemaPath: ['', Validators.required],
  });

  protected readonly showPalette = signal(true);

  constructor() {
    this.loadCatalog();

    // 初回は迷いやすいので、ViewFieldノードを1つ用意しておく
    // （fieldNameは空のまま。右ペインで入力してもらう）
    if (this.viewNodes().length === 0) {
      this.addViewFieldNode();
    }

    // カタログ選択→schemaPathを自動反映
    this.form
      .get('entitySchemaId')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((id) => {
        const picked = this.catalogItems().find((x) => x.id === id);
        if (!picked) return;
        const schemaPath = picked.schemaPath ?? `settings/test/entitySchema/${picked.id}`;
        this.form.patchValue({ schemaPath }, { emitEvent: false });
        this.loadEntitySchema(schemaPath);
      });

    this.form
      .get('schemaPath')
      ?.valueChanges.pipe(
        debounceTime(250),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((schemaPath) => {
        if (typeof schemaPath !== 'string' || !schemaPath.trim()) return;
        this.loadEntitySchema(schemaPath.trim());
      });
  }

  handlePickEntityField(fieldName: string): void {
    // クリックでも配置できるように、キャンバスの初期位置へ置く（ドラッグ&ドロップが基本）
    void this.reteCanvas?.addEntityFieldNode(fieldName, { x: 40, y: 120 });
  }

  addLiteral(kind: LiteralKind): void {
    void this.reteCanvas?.addLiteralNode(kind);
  }

  addComparator(op: ComparatorOp): void {
    void this.reteCanvas?.addComparatorNode(op);
  }

  addFormula(fn: FormulaFn): void {
    void this.reteCanvas?.addFormulaNode(fn);
  }

  loadCatalog(): void {
    this.loadingCatalog.set(true);
    this.catalogError.set(null);

    this.catalogService.getEntitySchemaCatalog().subscribe({
      next: (items) => {
        this.catalogItems.set(items);
        if (!this.form.get('entitySchemaId')?.value && items.length > 0) {
          const first = items[0];
          const schemaPath = first.schemaPath ?? `settings/test/entitySchema/${first.id}`;
          this.form.patchValue({ entitySchemaId: first.id, schemaPath }, { emitEvent: false });
          this.loadEntitySchema(schemaPath);
        }
        this.loadingCatalog.set(false);
      },
      error: (err) => {
        console.error('catalog load error', err);
        this.catalogError.set('カタログの取得に失敗しました。接続先とCORS設定を確認してください。');
        this.catalogItems.set([]);
        this.loadingCatalog.set(false);
      },
    });
  }

  loadEntitySchema(schemaPath: string): void {
    this.loadingEntitySchema.set(true);
    this.entitySchemaError.set(null);

    this.entitySchemaService.getEntitySchemaText(schemaPath).subscribe({
      next: (text) => {
        const trimmed = typeof text === 'string' ? text.trim() : '';
        const fields = this.extractEntityFields(trimmed);
        this.entityFields.set(fields);
        // rowExpands候補はentity fieldsから選ぶ前提なので、ここでは維持
        this.loadingEntitySchema.set(false);
        this.rebuildViewSchemaJson();
      },
      error: (err) => {
        console.error('entitySchema load error', err);
        this.entitySchemaError.set('EntitySchema の取得に失敗しました。schemaPath / bucketCode / CORS を確認してください。');
        this.entityFields.set([]);
        this.loadingEntitySchema.set(false);
        this.rebuildViewSchemaJson();
      },
    });
  }

  addViewFieldNode(): void {
    const id = crypto.randomUUID();
    const next: ViewFieldNodeState = { id, fieldName: '', fieldType: '', formula: '' };
    this.viewNodes.set([...this.viewNodes(), next]);
    this.selectedViewNodeId.set(id);
    this.rebuildViewSchemaJson();
  }

  removeSelectedViewFieldNode(): void {
    const id = this.selectedViewNodeId();
    if (!id) return;
    this.viewNodes.set(this.viewNodes().filter((n) => n.id !== id));
    // 接続も削除
    this.connections.set(this.connections().filter((c) => c.toViewNodeId !== id));
    this.selectedViewNodeId.set(null);
    this.rebuildViewSchemaJson();
  }

  handleSelectViewNode(id: string | null): void {
    this.selectedViewNodeId.set(id);
  }

  handleViewNodePatch(patch: Partial<ViewFieldNodeState> & { id: string }): void {
    const next = this.viewNodes().map((n) => (n.id === patch.id ? { ...n, ...patch } : n));
    this.viewNodes.set(next);
    this.rebuildViewSchemaJson();
  }

  handleRowExpandsChange(next: string[]): void {
    this.rowExpands.set(next);
    this.rebuildViewSchemaJson();
  }

  toggleRowExpand(fieldName: string, checked: boolean): void {
    const current = new Set(this.rowExpands());
    if (checked) current.add(fieldName);
    else current.delete(fieldName);
    this.rowExpands.set([...current]);
    this.rebuildViewSchemaJson();
  }

  handleConnectionsChange(next: ReteConnection[]): void {
    // 1ViewField=1接続を前提に、同じtoViewNodeIdは最後の1件だけ残す
    const map = new Map<string, ReteConnection>();
    next.forEach((c) => map.set(c.toViewNodeId, c));
    this.connections.set([...map.values()]);
    // fieldName 自動継承だけは維持（formula は graph 側で上書きする）
    const byTo = new Map(this.connections().map((c) => [c.toViewNodeId, c.fromEntityFieldName]));
    this.viewNodes.set(
      this.viewNodes().map((n) => {
        const from = byTo.get(n.id);
        if (!from) return n;
        if (n.overrideFieldName) return n;
        if (n.fieldName?.trim()) return n;
        return { ...n, fieldName: from };
      }),
    );
    this.rebuildViewSchemaJson();
  }

  handleViewNodeFormulaChange(formulaByViewId: Record<string, string>): void {
    const next = this.viewNodes().map((n) => {
      const f = formulaByViewId[n.id];
      if (!f) return n;
      return { ...n, formula: f };
    });
    this.viewNodes.set(next);
    this.rebuildViewSchemaJson();
  }

  private rebuildViewSchemaJson(): void {
    const out: ViewSchemaOut = {};
    const fields = this.viewNodes()
      .map((n) => {
        const fieldName = n.fieldName.trim();
        if (!fieldName) return null;
        const row: { fieldName: string; formula?: string; fieldType?: string } = { fieldName };
        if (n.formula?.trim()) row.formula = n.formula.trim();
        if (n.fieldType?.trim()) row.fieldType = n.fieldType.trim();
        return row;
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);

    if (fields.length > 0) out.fields = fields;
    const expands = this.rowExpands().filter((x) => !!x);
    if (expands.length > 0) out.rowExpands = expands;

    this.viewSchemaJson.set(JSON.stringify({ viewSchema: out }, null, 2));
  }

  private extractEntityFields(entitySchemaText: string): EntityFieldRow[] {
    try {
      const obj = JSON.parse(entitySchemaText) as any;
      const fields = Array.isArray(obj?.fields) ? obj.fields : [];
      return fields.flatMap((f: any) => {
        const fieldName = typeof f?.fieldName === 'string' ? f.fieldName.trim() : '';
        if (!fieldName) return [];
        return [
          {
            fieldName,
            displayName: typeof f?.displayName === 'string' ? f.displayName : undefined,
            fieldType: typeof f?.fieldType === 'string' ? f.fieldType : undefined,
            length: typeof f?.length === 'number' ? f.length : undefined,
          } satisfies EntityFieldRow,
        ];
      });
    } catch {
      return [];
    }
  }
}

